// The Inputs decision interface's reads: the SAME GETs the evidence view makes (lib/mackInputs.ts):
//   GET /api/mack/inputs           once on open, again on the server's mack_inputs_changed push or the
//                                  page's mack-inputs-updated event, 20 s timeout — exactly as useMackInputs
//   GET /api/mack/inputs/history   only when History is opened (field_id, offset, limit 50)
// The response is kept RAW (lib/mackInputsView.js reads it); nothing here writes. The Acquire control
// is the evidence view's own (refreshMackInputs in lib/mackInputs.ts, unchanged); after it answers, the
// page re-reads through reload() — the same GET /api/mack/inputs.
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { apiBase, authToken } from '../api/apiBase';
import { getRexySocket } from '../hooks/useRexySocket';
import { readInputs } from './mackInputsView';
import { subscribeMackInputsChanges } from './mackInputsNotifications';

export function useInputsRaw(suppliedToken?: string | null) { return useInputsRead(suppliedToken).read; }

export function useInputsRead(suppliedToken?: string | null) {
  const [read, setRead] = useState<{ response: any; notices: string[] } | null>(null);
  const current = useRef<AbortController | null>(null);
  const token = suppliedToken || authToken();
  const reload = useCallback(async () => {
    current.current?.abort(); const controller = new AbortController(); current.current = controller;
    const timer = window.setTimeout(() => controller.abort(), 20_000);
    try {
      const response = await axios.get(`${apiBase()}/mack/inputs`, { signal: controller.signal });
      if (current.current === controller) setRead(readInputs(response.data));
    } catch {
      if (current.current === controller && !controller.signal.aborted) setRead({ ...readInputs(null), notices: ['Inputs could not be read from the server.'] });
      else if (current.current === controller) setRead({ ...readInputs(null), notices: ['Inputs did not answer within 20 seconds.'] });
    } finally { window.clearTimeout(timer); }
  }, []);
  useEffect(() => {
    setRead(null); void reload();
    const socket = getRexySocket();
    const unsubscribe = subscribeMackInputsChanges(socket, () => { void reload(); });
    return () => { current.current?.abort(); unsubscribe(); };
  }, [token, reload]);
  return { read, reload };
}

export async function fetchInputsHistoryRaw(fieldId: string, offset: number): Promise<Record<string, unknown>[]> {
  const response = await axios.get(`${apiBase()}/mack/inputs/history`, { params: { field_id: fieldId, offset, limit: 50 } });
  const body = response.data && typeof response.data === 'object' ? response.data as Record<string, unknown> : null;
  if (!body || body.success !== true || body.field_id !== fieldId || body.offset !== offset || !Array.isArray(body.rows)) throw new Error('Inputs history unavailable');
  return body.rows as Record<string, unknown>[];
}
